import { useCallback, useEffect, useRef, useState } from 'react';

import type { DragDirection } from './types';

type DragState = {
  startY: number | undefined;
  currentY: number | undefined;
  startX: number | undefined;
  currentX: number | undefined;
  transformY: number;
  transformX: number;
  shouldClose: boolean;
  isDragging: boolean;
  popoverHeight: number;
  popoverWidth: number;
  activePointerId: number | undefined;
  tapStartTime: number | undefined;
  isHorizontalDrag: boolean;
};

type UseDragHandlingProps = {
  enabled?: boolean;
  onClose: () => void;
  direction: DragDirection;
};

const DRAG_THRESHOLD = 0.1;
const TAP_THRESHOLD = 300; // milliseconds

const INITIAL_DRAG_STATE: DragState = {
  startY: undefined,
  currentY: undefined,
  startX: undefined,
  currentX: undefined,
  transformY: 0,
  transformX: 0,
  shouldClose: false,
  isDragging: false,
  popoverHeight: 0,
  popoverWidth: 0,
  activePointerId: undefined,
  tapStartTime: undefined,
  isHorizontalDrag: false
};

export const useDragHandling = ({ enabled = true, onClose, direction }: UseDragHandlingProps) => {
  const [dragState, setDragState] = useState<DragState>(INITIAL_DRAG_STATE);
  const popoverRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | undefined>();
  const lastYRef = useRef<number>(0);
  const lastXRef = useRef<number>(0);
  const capturedPointerIdRef = useRef<number | undefined>(undefined);
  const capturedElementRef = useRef<Element | null>(null);

  const resetDragState = useCallback(() => {
    const capturedElement = capturedElementRef.current;
    if (capturedPointerIdRef.current !== undefined && capturedElement) {
      try {
        capturedElement.releasePointerCapture(capturedPointerIdRef.current);
      } catch (error) {
        console.warn('Failed to release pointer capture:', error);
      }
      capturedElementRef.current = null;
      capturedPointerIdRef.current = undefined;
    }

    lastYRef.current = 0;
    lastXRef.current = 0;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }
    setDragState(INITIAL_DRAG_STATE);
  }, []);

  const handlePointerUpOrCancel = useCallback(
    (event: PointerEvent) => {
      if (event.pointerId !== dragState.activePointerId) return;

      capturedPointerIdRef.current = undefined;

      // Check if it was a tap (quick press and release)
      const tapDuration = Date.now() - (dragState.tapStartTime || 0);
      if (tapDuration < TAP_THRESHOLD && !dragState.isDragging) {
        onClose();
        resetDragState();
        return;
      }

      if (dragState.isDragging && dragState.shouldClose) {
        onClose();
      }

      resetDragState();
    },
    [
      dragState.activePointerId,
      dragState.isDragging,
      dragState.shouldClose,
      dragState.tapStartTime,
      onClose,
      resetDragState
    ]
  );

  const updateDragState = useCallback(
    (currentY: number, currentX: number) => {
      const { startY, startX, popoverHeight, popoverWidth, isHorizontalDrag } = dragState;

      if (isHorizontalDrag) {
        if (startX === undefined || popoverWidth === 0) return;
        const dragDistance = currentX - startX;
        if (dragDistance > 0) return; // Only allow dragging to the left

        const shouldClose = Math.abs(dragDistance) > popoverWidth * DRAG_THRESHOLD;

        setDragState(prev => ({
          ...prev,
          currentX,
          transformX: dragDistance,
          shouldClose,
          isDragging: true
        }));
      } else {
        if (startY === undefined || popoverHeight === 0) return;
        const dragDistance = currentY - startY;
        if (dragDistance < 0) return;

        const shouldClose = dragDistance > popoverHeight * DRAG_THRESHOLD;

        setDragState(prev => ({
          ...prev,
          currentY,
          transformY: dragDistance,
          shouldClose,
          isDragging: true
        }));
      }
    },
    [dragState]
  );

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      if (!dragState.activePointerId || event.pointerId !== dragState.activePointerId) return;

      const currentY = event.clientY;
      const currentX = event.clientX;
      if (currentY === lastYRef.current && currentX === lastXRef.current) return;
      lastYRef.current = currentY;
      lastXRef.current = currentX;

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = requestAnimationFrame(() => {
        updateDragState(currentY, currentX);
      });
    },
    [dragState.activePointerId, updateDragState]
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<Element>) => {
      if (dragState.activePointerId !== undefined || !popoverRef.current) return;

      const targetElement = e.currentTarget;
      const currentY = e.clientY;
      const currentX = e.clientX;
      const popoverHeight = popoverRef.current.offsetHeight;
      const popoverWidth = popoverRef.current.offsetWidth;

      lastYRef.current = currentY;
      lastXRef.current = currentX;

      setDragState(prev => ({
        ...prev,
        startY: currentY,
        currentY: currentY,
        startX: currentX,
        currentX: currentX,
        popoverHeight: popoverHeight,
        popoverWidth: popoverWidth,
        activePointerId: e.pointerId,
        tapStartTime: Date.now(),
        isHorizontalDrag: direction === 'horizontal'
      }));

      targetElement.setPointerCapture(e.pointerId);
      capturedPointerIdRef.current = e.pointerId;
      capturedElementRef.current = targetElement;

      e.preventDefault();
    },
    [dragState.activePointerId, direction]
  );

  useEffect(() => {
    if (dragState.activePointerId === undefined) {
      return;
    }

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUpOrCancel);
    window.addEventListener('pointercancel', handlePointerUpOrCancel);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUpOrCancel);
      window.removeEventListener('pointercancel', handlePointerUpOrCancel);

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }

      capturedPointerIdRef.current = undefined;
      capturedElementRef.current = null;
    };
  }, [dragState.activePointerId, handlePointerMove, handlePointerUpOrCancel]);

  useEffect(() => {
    if (!enabled && dragState.activePointerId !== undefined) {
      resetDragState();
    }
  }, [enabled, dragState.activePointerId, resetDragState]);

  return {
    dragState,
    popoverRef,
    handlePointerDown,
    reset: resetDragState
  };
};
